import { Handle, NodeProps, Position } from '@xyflow/react'
import { ProcessFlowDiagramBuilderContext } from 'features/processFlowDiagrams/contexts/ProcessFlowDiagramBuilderContext'
import { resolveHighlightColor } from 'features/processFlowDiagrams/utils/helpers'
import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { PfdNode } from 'types'

const GhostNodeContainer = styled.div<{
  $rotation: number
  $isSelected: boolean
  $highlightColor?: string | null
}>`
  width: 1px;
  height: 1px;
  transform: rotate(${({ $rotation }) => $rotation}deg);

  ${({ $isSelected, $highlightColor }) =>
    $isSelected &&
    $highlightColor &&
    `
    filter: drop-shadow(0 0 6px ${$highlightColor}) drop-shadow(0 0 12px rgba(255, 255, 255, 0.4));
  `}
`

const GhostNodeCircle = styled.div<{
  $rotation: number
  $isSelected: boolean
  $highlightColor?: string | null
}>`
  width: 0.5rem;
  height: 0.5rem;
  background-color: transparent;
  border-radius: 50%;
  transform: rotate(${({ $rotation }) => $rotation}deg);

  ${({ $isSelected, $highlightColor }) =>
    $isSelected &&
    $highlightColor &&
    `
    filter: drop-shadow(0 0 6px ${$highlightColor}) drop-shadow(0 0 12px rgba(255, 255, 255, 0.4));
  `}
`

export const GhostNode: React.FC<NodeProps<PfdNode>> = ({ id, data }) => {
  const { editingEnabled, selectedNode } = useContext(
    ProcessFlowDiagramBuilderContext
  )

  const isSelected = useMemo(() => {
    if (!selectedNode) return false
    return selectedNode.id === id
  }, [selectedNode, id])

  const selectedHighlight = useMemo(() => {
    if (!isSelected) return undefined
    const status = data?.status
    return resolveHighlightColor(status)
  }, [isSelected, data])
  return (
    <>
      {!editingEnabled ? (
        <GhostNodeContainer
          $rotation={data?.rotation ?? 0}
          $isSelected={isSelected}
          $highlightColor={selectedHighlight}>
          <Handle
            key='left'
            id='left-source'
            type='source'
            position={Position.Left}
            isConnectable={true}
          />
          <Handle
            key='top'
            id='top-source'
            type='source'
            position={Position.Top}
            isConnectable={true}
          />
          <Handle
            key='bottom'
            id='bottom-source'
            type='source'
            position={Position.Bottom}
            isConnectable={true}
          />
          <Handle
            key='right'
            id='right-source'
            type='source'
            position={Position.Right}
            isConnectable={true}
          />
        </GhostNodeContainer>
      ) : (
        <GhostNodeCircle
          $rotation={data?.rotation ?? 0}
          $isSelected={isSelected}
          $highlightColor={selectedHighlight}>
          <Handle
            key='left'
            id='left-source'
            type='source'
            position={Position.Left}
            isConnectable={true}
            style={{ background: 'white', left: '50%' }}
          />
          <Handle
            key='top'
            id='top-source'
            type='source'
            position={Position.Top}
            isConnectable={true}
            style={{ background: 'white', top: '50%' }}
          />
          <Handle
            key='bottom'
            id='bottom-source'
            type='source'
            position={Position.Bottom}
            isConnectable={true}
            style={{
              background: 'white',
              bottom: '50%',
            }}
          />
          <Handle
            key='right'
            id='right-source'
            type='source'
            position={Position.Right}
            isConnectable={true}
            style={{ background: 'white', right: '50%' }}
          />
        </GhostNodeCircle>
      )}
    </>
  )
}
